import { getAddress, isAddress } from "viem";
import {
  parseJsonBytesWithUniqueObjectKeys,
  parseX402Review,
  isX402BoonBackedReview,
  type X402BoonBackedReview,
} from "@boon/x402-route";

export const X402_REVIEW_SIGNING_PAYLOAD_VERSION = "boon.x402-review-sign/v1" as const;

export interface X402ReviewSigningPayload {
  version: typeof X402_REVIEW_SIGNING_PAYLOAD_VERSION;
  review: X402BoonBackedReview;
  expectedSigner: `0x${string}`;
  domain: {
    chainId: 8453;
    verifyingContract: `0x${string}`;
  };
  issuedAt: number;
  expiresAt: number;
}

function safeInteger(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || Number(value) <= 0) throw new Error(`${label} is invalid.`);
  return Number(value);
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("The signing payload is malformed.");
  return value as Record<string, unknown>;
}

function decodeBase64Url(value: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) throw new Error("The signing payload encoding is invalid.");
  const padded = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const decoded = atob(padded);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

export function readX402ReviewSigningPayload(hash: string, now = Math.floor(Date.now() / 1_000)): X402ReviewSigningPayload {
  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const encoded = params.get("payload");
  if (!encoded) throw new Error("This signing link does not contain a review payload.");
  const parsed = parseJsonBytesWithUniqueObjectKeys(decodeBase64Url(encoded), {
    maxBytes: 8 * 1024,
    label: "x402 browser signing payload",
  });
  const value = record(parsed);
  if (value.version !== X402_REVIEW_SIGNING_PAYLOAD_VERSION) throw new Error("This review signing link uses an unsupported version.");
  const expectedSigner = value.expectedSigner;
  if (typeof expectedSigner !== "string" || !isAddress(expectedSigner)) throw new Error("The required signer is invalid.");
  const domain = record(value.domain);
  if (domain.chainId !== 8453 || typeof domain.verifyingContract !== "string" || !isAddress(domain.verifyingContract)) {
    throw new Error("The review signing domain is not canonical Base.");
  }
  const issuedAt = safeInteger(value.issuedAt, "issuedAt");
  const expiresAt = safeInteger(value.expiresAt, "expiresAt");
  if (expiresAt <= issuedAt || expiresAt - issuedAt > 30 * 60) throw new Error("The review signing window is invalid.");
  if (issuedAt > now + 60) throw new Error("The review signing link was issued in the future.");
  if (now > expiresAt) throw new Error("This review signing link expired. Run the CLI command again.");
  const review = parseX402Review(value.review);
  if (!isX402BoonBackedReview(review)) throw new Error("The browser signing page only accepts Boon-backed review payloads.");
  return {
    version: X402_REVIEW_SIGNING_PAYLOAD_VERSION,
    review,
    expectedSigner: getAddress(expectedSigner),
    domain: {
      chainId: 8453,
      verifyingContract: getAddress(domain.verifyingContract),
    },
    issuedAt,
    expiresAt,
  };
}
